import { api, handleApiError } from '../client'
import type {
  ApprovalWorkflow,
  ApprovalWorkflowDetail,
  ApprovalStage,
  StageApprover,
  ApprovalRequest,
  ApprovalRequestDetail,
  ApprovalComment,
  PendingApproval,
  CreateApprovalWorkflowRequest,
  UpdateApprovalWorkflowRequest,
  CreateApprovalStageRequest,
  UpdateApprovalStageRequest,
  AddStageApproverRequest,
  SubmitForApprovalRequest,
  ProcessApprovalRequest,
  AddApprovalCommentRequest,
  ApprovalRequestStatus,
} from '../types'

export interface ApprovalRequestFilters {
  status?: ApprovalRequestStatus
  templateId?: string
}

export const approvalWorkflowService = {
  // ==================== Workflows ====================

  /**
   * Gets all approval workflows for the current tenant.
   */
  async getWorkflows(): Promise<ApprovalWorkflow[]> {
    try {
      const response = await api.get<ApprovalWorkflow[]>('/v1/approval-workflows')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a specific approval workflow by ID with all stages.
   */
  async getWorkflow(id: string): Promise<ApprovalWorkflowDetail> {
    try {
      const response = await api.get<ApprovalWorkflowDetail>(`/v1/approval-workflows/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Creates a new approval workflow.
   */
  async createWorkflow(data: CreateApprovalWorkflowRequest): Promise<ApprovalWorkflow> {
    try {
      const response = await api.post<ApprovalWorkflow>('/v1/approval-workflows', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing approval workflow.
   */
  async updateWorkflow(id: string, data: UpdateApprovalWorkflowRequest): Promise<ApprovalWorkflow> {
    try {
      const response = await api.put<ApprovalWorkflow>(`/v1/approval-workflows/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes an approval workflow.
   */
  async deleteWorkflow(id: string): Promise<void> {
    try {
      await api.delete(`/v1/approval-workflows/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Stages ====================

  /**
   * Adds a stage to an approval workflow.
   */
  async addStage(workflowId: string, data: CreateApprovalStageRequest): Promise<ApprovalStage> {
    try {
      const response = await api.post<ApprovalStage>(
        `/v1/approval-workflows/${workflowId}/stages`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates a stage in an approval workflow.
   */
  async updateStage(
    workflowId: string,
    stageId: string,
    data: UpdateApprovalStageRequest
  ): Promise<ApprovalStage> {
    try {
      const response = await api.put<ApprovalStage>(
        `/v1/approval-workflows/${workflowId}/stages/${stageId}`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Removes a stage from an approval workflow.
   */
  async removeStage(workflowId: string, stageId: string): Promise<void> {
    try {
      await api.delete(`/v1/approval-workflows/${workflowId}/stages/${stageId}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Reorders stages in an approval workflow.
   */
  async reorderStages(workflowId: string, stageIds: string[]): Promise<void> {
    try {
      await api.post(`/v1/approval-workflows/${workflowId}/stages/reorder`, { stageIds })
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Approvers ====================

  /**
   * Adds an approver to a stage.
   */
  async addApprover(
    workflowId: string,
    stageId: string,
    data: AddStageApproverRequest
  ): Promise<StageApprover> {
    try {
      const response = await api.post<StageApprover>(
        `/v1/approval-workflows/${workflowId}/stages/${stageId}/approvers`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Removes an approver from a stage.
   */
  async removeApprover(workflowId: string, stageId: string, approverId: string): Promise<void> {
    try {
      await api.delete(
        `/v1/approval-workflows/${workflowId}/stages/${stageId}/approvers/${approverId}`
      )
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Approval Requests ====================

  /**
   * Gets all approval requests with optional filters.
   */
  async getRequests(filters?: ApprovalRequestFilters): Promise<ApprovalRequest[]> {
    try {
      const params = new URLSearchParams()
      if (filters?.status) params.append('status', filters.status)
      if (filters?.templateId) params.append('templateId', filters.templateId)

      const queryString = params.toString()
      const url = `/v1/approval-requests${queryString ? `?${queryString}` : ''}`
      const response = await api.get<ApprovalRequest[]>(url)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets pending approvals for the current user.
   * @param tenantId Optional tenant ID to filter by (admin only)
   */
  async getPendingApprovals(tenantId?: string): Promise<PendingApproval[]> {
    try {
      const params = tenantId ? { tenantId } : undefined
      const response = await api.get<PendingApproval[]>('/v1/approval-requests/pending', { params })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets pending approvals count for the current user.
   */
  async getPendingApprovalsCount(): Promise<number> {
    try {
      const response = await api.get<{ count: number }>('/v1/approval-requests/pending/count')
      return response.data.count
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a specific approval request by ID.
   */
  async getRequest(id: string): Promise<ApprovalRequestDetail> {
    try {
      const response = await api.get<ApprovalRequestDetail>(`/v1/approval-requests/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Submits a template for approval.
   */
  async submitForApproval(
    templateId: string,
    data?: SubmitForApprovalRequest
  ): Promise<ApprovalRequest> {
    try {
      const response = await api.post<ApprovalRequest>(
        `/v1/templates/${templateId}/approval/submit`,
        data || {}
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Processes an approval action (approve, reject, request changes).
   */
  async processApproval(requestId: string, data: ProcessApprovalRequest): Promise<ApprovalRequest> {
    try {
      const response = await api.post<ApprovalRequest>(
        `/v1/approval-requests/${requestId}/process`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Adds a comment to an approval request.
   */
  async addComment(requestId: string, data: AddApprovalCommentRequest): Promise<ApprovalComment> {
    try {
      const response = await api.post<ApprovalComment>(
        `/v1/approval-requests/${requestId}/comments`,
        data
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Resolves a comment.
   */
  async resolveComment(requestId: string, commentId: string): Promise<ApprovalComment> {
    try {
      const response = await api.post<ApprovalComment>(
        `/v1/approval-requests/${requestId}/comments/${commentId}/resolve`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Cancels an approval request.
   */
  async cancelRequest(requestId: string): Promise<void> {
    try {
      await api.post(`/v1/approval-requests/${requestId}/cancel`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets approval history for a template.
   */
  async getApprovalHistory(templateId: string): Promise<ApprovalRequest[]> {
    try {
      const response = await api.get<ApprovalRequest[]>(
        `/v1/templates/${templateId}/approval/history`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets the current approval request for a template (if any).
   */
  async getCurrentApproval(templateId: string): Promise<ApprovalRequest | null> {
    try {
      const response = await api.get<ApprovalRequest>(
        `/v1/templates/${templateId}/approval/current`
      )
      return response.data
    } catch (error) {
      // Return null if no current approval exists
      return null
    }
  },
}
